import classNames from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Caption from '../../../../../components/caption/Caption';
import { SimpleDrawingSvg } from '../../../../drawing/DrawingSvg';
import { getIsFeatureEnabledForCurrentUser } from '../../../../feature/elementFeatureSelector';
import ImageModalToolbarSecondaryTools from '../../toolbar/ImageModalToolbarSecondaryTools';
import { getImageSource } from '../../../imageHelper';
import { ExperimentId } from '../../../../../../common/experiments/experimentsConstants';
import type { MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import type { UserFeatureUsageSummary } from '../../../../../../common/users/userFeatureUsageTypes';
import {
    getCaption,
    getElementId,
    getHasDrawing,
    getImageProp,
    getShowCaption,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { getHeight, getWidth } from '../../../../../../common/maths/geometry/rect';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { getImageModalEditorDrawingViewBox } from '../../content/utils/imageModalEditorDrawingUtils';
import { fitSizeWithinBounds } from '../../imageModalSizingUtils';
import {
    getLargestImageSize,
    getOriginalImage,
    getOriginalImageHeight,
    getOriginalImageWidth,
} from '../../imageModalUtils';
import type { ImageModalV2EditModeProps } from '../imageModalV2Types';
import ImageModalV2BackgroundToolbar from '../toolbar/ImageModalV2BackgroundToolbar';
import {
    IMAGE_MODAL_V2_OPERATION_MESSAGE,
    type ImageModalV2OperationToastState,
} from '../toolbar/imageModalV2OperationToastTypes';
import type { ImageModalV2ErrorToastState } from '../toolbar/imageModalV2ErrorToastTypes';
import {
    applyStagedBackgroundRemoval,
    isBackgroundRemovalActive,
} from '../../backgroundRemoval/imageModalBackgroundRemovalUtils';
import { useBackgroundRemovalCrop } from '../../content/hooks/useBackgroundRemovalCrop';
import { useBackgroundRemovalWipeOverlay } from '../../content/hooks/useBackgroundRemovalWipeOverlay';
import { useImageModalBackgroundRemovalProgress } from './hooks/useImageModalBackgroundRemovalProgress';
import { useImageModalV2BackgroundPreview } from './hooks/useImageModalV2BackgroundPreview';
import { useImageModalV2BackgroundRemovalState } from './hooks/useImageModalV2BackgroundRemovalState';
import { useImageModalV2BackgroundSave } from './hooks/useImageModalV2BackgroundSave';
import { yieldToNextPaint } from '../utils/yieldToNextPaint';

import '../../content/ImageModalEditor.scss';

type ImageModalV2BackgroundEditorProps = ImageModalV2EditModeProps & {
    editorBounds: { width: number; height: number };
    cancelEditing: () => void;
    afterSave: (shouldWaitForViewLoad?: boolean) => void;
    onEditorReadyChange?: (ready: boolean) => void;
    onOperationToastChange: (toast: ImageModalV2OperationToastState | null) => void;
    onErrorToastChange: (toast: ImageModalV2ErrorToastState | null) => void;
};

/** Cropper-free owner for staged background-removal preview and persistence. */
const ImageModalV2BackgroundEditor: React.FC<ImageModalV2BackgroundEditorProps> = ({
    element,
    gridSize,
    attachment,
    editorBounds,
    cancelEditing,
    afterSave,
    beginSaveHandoff,
    cancelSaveHandoff,
    setCaptionHiddenOnModalClose,
    onEditorReadyChange,
    onOperationToastChange,
    onErrorToastChange,
}) => {
    const useSecureMediaUrl = true;
    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );
    const elementId = getElementId(element);
    const [stagedActive, setStagedActive] = useState(() => isBackgroundRemovalActive(element));
    const [deferImageCommit, setDeferImageCommit] = useState(false);
    const [holdWipeAnimation, setHoldWipeAnimation] = useState(false);
    const previousElementIdRef = useRef(elementId);
    const elementRef = useRef(element);
    elementRef.current = element;

    useLayoutEffect(() => {
        if (previousElementIdRef.current === elementId) return;
        previousElementIdRef.current = elementId;
        setStagedActive(isBackgroundRemovalActive(elementRef.current));
    }, [elementId]);

    const previewElement = useMemo(() => applyStagedBackgroundRemoval(element, stagedActive), [element, stagedActive]);
    const previewImage = useMemo(() => asObject<MNImageContent>(getImageProp(previewElement)), [previewElement]);
    const previewSourceUrl = useMemo(
        () =>
            previewImage
                ? getImageSource({
                      imageDetails: previewImage,
                      imageSize: getLargestImageSize(previewImage),
                      elementId,
                      useSecureMediaUrl,
                      renderSecureImagesThroughCF,
                  })
                : undefined,
        [elementId, previewImage, renderSecureImagesThroughCF],
    );
    // Progress ticks re-render this component frequently; key the preview identity off the variant URL
    // strings so effects only restart when the underlying sources actually change.
    const previewIdentity = useMemo(
        () =>
            [elementId, previewImage?.original, previewImage?.large, previewImage?.regular, previewImage?.thumb].join(
                '|',
            ),
        [elementId, previewImage],
    );

    const { backgroundRemovalEditSource, imageType, createInactiveRenderedVariant } = useBackgroundRemovalCrop({
        element,
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });
    const [previewIsReady, setPreviewIsReady] = useState(false);
    const [hasPreviewPainted, setHasPreviewPainted] = useState(false);

    const { backgroundRemovalApiProgress, backgroundRemovalUploadProgress, setBackgroundRemovalProgress } =
        useImageModalBackgroundRemovalProgress(elementId);
    const [backgroundRemovalUsage, setBackgroundRemovalUsage] = useState<UserFeatureUsageSummary | null>(null);
    const backgroundRemovalState = useImageModalV2BackgroundRemovalState({
        editorImageLoadKey: previewIdentity,
        editorIsReady: previewIsReady,
        backgroundRemovalApiProgress,
        backgroundRemovalUploadProgress,
    });
    const { displayUrl, commitPendingPreview } = useImageModalV2BackgroundPreview({
        sourceUrl: previewSourceUrl,
        imageType,
        sourceIdentity: previewIdentity,
        deferImageCommit,
        onDeferredImageReady: backgroundRemovalState.markProcessedImageDecoded,
        onPreviewReadyChange: (ready) => {
            setPreviewIsReady(ready);
            if (ready) backgroundRemovalState.handleEditorImageReady(true);
            if (!ready) {
                setHasPreviewPainted(false);
                onEditorReadyChange?.(false);
            }
        },
    });
    const operationMessage = backgroundRemovalState.toast?.message;
    const operationProgress = backgroundRemovalState.toast?.progress;
    useEffect(() => {
        onOperationToastChange(
            operationMessage
                ? { message: operationMessage, progress: operationProgress || null, usage: backgroundRemovalUsage }
                : null,
        );
    }, [onOperationToastChange, operationMessage, operationProgress, backgroundRemovalUsage]);
    useEffect(() => () => onOperationToastChange(null), [onOperationToastChange]);
    const handlePreviewPaint = useCallback(() => {
        setHasPreviewPainted(true);
        onEditorReadyChange?.(true);
    }, [onEditorReadyChange]);
    const previewRef = useCallback(
        (node: HTMLImageElement | null) => {
            if (node?.complete && node.naturalWidth > 0) handlePreviewPaint();
        },
        [handlePreviewPaint],
    );

    const { isCreatingRenderedVariant, changeStagedActive, save, discard } = useImageModalV2BackgroundSave({
        element,
        stagedActive,
        setStagedActive,
        createInactiveRenderedVariant,
        cancelEditing,
        afterSave,
        setCaptionHiddenOnModalClose,
    });

    useLayoutEffect(() => {
        if (!backgroundRemovalState.shouldDeferEditorImageCommit) return;
        setDeferImageCommit(true);
    }, [backgroundRemovalState.shouldDeferEditorImageCommit]);

    const cancelDeferredCommitReleaseRef = useRef<(() => void) | null>(null);
    const releaseCommitForWipe = useCallback(() => {
        cancelDeferredCommitReleaseRef.current?.();
        let cancelled = false;
        cancelDeferredCommitReleaseRef.current = () => {
            cancelled = true;
        };

        void (async () => {
            await yieldToNextPaint();
            if (cancelled) return;
            setHoldWipeAnimation(true);
            setDeferImageCommit(false);

            await yieldToNextPaint();
            if (cancelled) return;
            commitPendingPreview();

            await yieldToNextPaint();
            if (cancelled) return;
            cancelDeferredCommitReleaseRef.current = null;
            setHoldWipeAnimation(false);
        })();
    }, [commitPendingPreview]);

    useEffect(() => {
        if (backgroundRemovalState.shouldDeferEditorImageCommit) return;
        if (!deferImageCommit) return;
        releaseCommitForWipe();
    }, [backgroundRemovalState.shouldDeferEditorImageCommit, deferImageCommit, releaseCommitForWipe]);

    useEffect(
        () => () => {
            cancelDeferredCommitReleaseRef.current?.();
        },
        [],
    );

    const { handleImageDataUrlLoad, handleWipeAnimationEnd, wipeOverlay } = useBackgroundRemovalWipeOverlay({
        enabled: backgroundRemovalState.shouldAnimateEditorImageReplacement || deferImageCommit || holdWipeAnimation,
        deferWipeMount: deferImageCommit,
        editorImageLoadKey: previewIdentity,
        editorIsReady: previewIsReady && !deferImageCommit && !holdWipeAnimation,
        imageTransform: 'none',
        onWipeSettled: backgroundRemovalState.completeEditorImageReplacement,
    });

    useEffect(() => {
        if (displayUrl) handleImageDataUrlLoad(displayUrl);
    }, [displayUrl, handleImageDataUrlLoad]);

    const imageWidth =
        getWidth(previewImage) || getOriginalImageWidth({ element: previewElement, gridSize, attachment });
    const imageHeight =
        getHeight(previewImage) || getOriginalImageHeight({ element: previewElement, gridSize, attachment });
    const previewSize = fitSizeWithinBounds({ width: imageWidth, height: imageHeight }, editorBounds);
    const backgroundRemovalSource = backgroundRemovalEditSource || getOriginalImage(element);
    const backgroundRemovalSourceUrl = useMemo(
        () =>
            getImageSource({
                imageDetails: backgroundRemovalSource,
                imageSize: getLargestImageSize(backgroundRemovalSource),
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        [backgroundRemovalSource, elementId, renderSecureImagesThroughCF],
    );
    const controlsDisabled =
        !previewIsReady ||
        !hasPreviewPainted ||
        isCreatingRenderedVariant ||
        backgroundRemovalState.controlsDisabled ||
        deferImageCommit ||
        holdWipeAnimation;
    const toolbarClassName = 'ImageEditorModalToolbar ImageEditorModalToolbar-v2';
    const handleSave = useCallback(async () => {
        beginSaveHandoff();
        const didSave = await save(() => {
            onOperationToastChange({
                message: IMAGE_MODAL_V2_OPERATION_MESSAGE.SAVING_CHANGES,
                progress: null,
            });
        });
        if (!didSave) {
            cancelSaveHandoff();
            onOperationToastChange(null);
        }
    }, [beginSaveHandoff, cancelSaveHandoff, onOperationToastChange, save]);

    return (
        <>
            <div
                className={classNames('image-modal-content image-modal-content-v2', {
                    'is-awaiting-preview-paint': !hasPreviewPainted,
                })}
                style={{ width: previewSize.width }}
            >
                <div className="image-container">
                    <div className="ImageEditor ImageEditor-v2">
                        <div
                            // The checkerboard stays behind the preview at all times: an opaque preview
                            // fully covers it, and background toggles never flash the modal background
                            // through transparent pixels while the next variant loads in.
                            className="editor editor-v2 background-removal-transparent-preview"
                            style={{ width: previewSize.width, height: previewSize.height }}
                        >
                            {displayUrl && (
                                <img
                                    ref={previewRef}
                                    className="editor-source-image ImageModalV2BackgroundEditor-preview"
                                    src={displayUrl}
                                    alt=""
                                    draggable={false}
                                    style={{ width: previewSize.width, height: previewSize.height }}
                                    onLoad={handlePreviewPaint}
                                />
                            )}
                            {wipeOverlay && (
                                <div
                                    key={wipeOverlay.renderKey}
                                    className={classNames('bg-removal-wipe-overlay', {
                                        'is-wiping': wipeOverlay.wiping,
                                    })}
                                    onAnimationEnd={handleWipeAnimationEnd}
                                    aria-hidden
                                >
                                    <div
                                        className="bg-removal-wipe-overlay-image"
                                        style={{
                                            width: previewSize.width,
                                            height: previewSize.height,
                                            transform: wipeOverlay.transform,
                                        }}
                                    >
                                        <img src={wipeOverlay.dataUrl} alt="" draggable={false} />
                                    </div>
                                </div>
                            )}
                            {getHasDrawing(previewElement) && hasPreviewPainted && (
                                <div className="drawing-svg-container">
                                    <div className="drawing-wrapper">
                                        <SimpleDrawingSvg
                                            element={previewElement}
                                            viewBox={getImageModalEditorDrawingViewBox(previewElement, false)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Caption
                        element={previewElement}
                        textContent={getCaption(previewElement)}
                        captionVisible={getShowCaption(previewElement)}
                    />
                </div>
            </div>

            <ImageModalToolbarSecondaryTools className={toolbarClassName}>
                <ImageModalV2BackgroundToolbar
                    element={previewElement}
                    cropSourceImage={backgroundRemovalSource}
                    largestImageUrl={backgroundRemovalSourceUrl}
                    imageType={imageType}
                    progress={backgroundRemovalState.buttonProgress}
                    setProgress={setBackgroundRemovalProgress}
                    onUsageChange={setBackgroundRemovalUsage}
                    ignoreActions={controlsDisabled}
                    stagedActive={stagedActive}
                    storedActive={isBackgroundRemovalActive(element)}
                    onChangeStagedActive={(active) => void changeStagedActive(active)}
                    onErrorToastChange={onErrorToastChange}
                    onCancelClick={discard}
                    onSaveClick={() => void handleSave()}
                />
            </ImageModalToolbarSecondaryTools>
        </>
    );
};

export default ImageModalV2BackgroundEditor;
